import type { CampaignSearchTerm, CampaignSearchTermsResponse } from "@adpilot/shared";
import { expressionTypeLabel } from "@adpilot/shared";
import {
  IconArrowDown,
  IconArrowsUpDown,
  IconArrowUp,
  IconDownload,
  IconMaximize,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnCustomizerDialog } from "@/components/campaign/ColumnCustomizerDialog";
import { ControlButton } from "@/components/patterns/control-button";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { FilterSelect } from "@/components/patterns/filter-select";
import { SearchInput } from "@/components/patterns/SearchInput";
import { UnavailableControl } from "@/components/patterns/unavailable-control";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogHeader, DialogPopup, DialogTitle } from "@/components/ui/dialog";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  aggregateGroups,
  csvDocument,
  downloadCsv,
  formatGroupMetric,
  type GroupColumn,
  groupMetric,
  type MetricSource,
  MONETARY_COLUMNS,
} from "../ad-groups/ad-groups-table-model";

export interface CampaignSearchTermsTableProps {
  campaignId: string;
  data?: CampaignSearchTermsResponse;
  previousData?: CampaignSearchTermsResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  comparison: boolean;
  onComparisonChange: (value: boolean) => void;
  comparisonLoading?: boolean;
  comparisonError?: boolean;
  onComparisonRetry?: () => void;
  renderAdGroupLink?: (adGroupId: string, name: string) => ReactNode;
}

/** Metric columns the search-term report can back (no units, no same-SKU split). */
const METRIC_COLUMNS = [
  "cpc",
  "cpa",
  "ctr",
  "cvr",
  "acos",
  "roas",
  "spend",
  "impressions",
  "clicks",
  "orders",
  "sales",
] as const satisfies readonly GroupColumn[];
type MetricColumn = (typeof METRIC_COLUMNS)[number];
const IDENTITY_COLUMNS = ["matchedTarget", "adGroup", "aiSuggestion"] as const;
const COLUMNS = [...IDENTITY_COLUMNS, ...METRIC_COLUMNS] as const;
type Column = (typeof COLUMNS)[number];
type SortKey = Column | "name";
const FIXED_COLUMNS = ["name"] as const;
const ALL_COLUMNS: readonly ((typeof FIXED_COLUMNS)[number] | Column)[] = [
  ...FIXED_COLUMNS,
  ...COLUMNS,
];
const isMetric = (key: Column): key is MetricColumn =>
  (METRIC_COLUMNS as readonly string[]).includes(key);
const MATCH_FILTERS = ["all", "exact", "phrase", "broad", "auto"] as const;
const SUGGESTION_FILTERS = ["all", "harvest", "negative", "observe"] as const;
const PAGE_SIZES = [25, 50, 100] as const;
const NAME_STICKY =
  "sticky left-0 z-20 min-w-56 border-r border-line shadow-[4px_0_8px_-7px_rgb(15_23_42_/_0.45)]";
const SUGGESTION_VARIANT: Record<
  CampaignSearchTerm["aiSuggestion"],
  "success" | "warning" | "neutral"
> = { harvest: "success", negative: "warning", observe: "neutral" };

/** Search terms always come from report rows, so a metric snapshot is always "captured". */
const asMetricSource = (term: CampaignSearchTerm): MetricSource => ({
  hasMetrics: true,
  metrics: term.metrics,
  skuBreakdown: null,
});

export function CampaignSearchTermsTable(props: CampaignSearchTermsTableProps) {
  const { t, i18n } = useTranslation("campaign");
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`detail.searchTermsList.${key}`, options);
  const shared = (key: string, options?: Record<string, unknown>) =>
    t(`detail.adGroupsList.${key}`, options);
  const [search, setSearch] = useState("");
  const [match, setMatch] = useState<(typeof MATCH_FILTERS)[number]>("all");
  const [suggestion, setSuggestion] = useState<(typeof SUGGESTION_FILTERS)[number]>("all");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<number>(PAGE_SIZES[0]);
  const [sort, setSort] = useState<{ key: SortKey; direction: "asc" | "desc" }>({
    key: "spend",
    direction: "desc",
  });
  const [columns, setColumns] = useState<readonly Column[]>(COLUMNS);
  const [expanded, setExpanded] = useState(false);

  const all = props.data?.connected ? props.data.searchTerms : [];
  const matchOf = (term: CampaignSearchTerm) =>
    term.expressionType ? "auto" : (term.keywordMatchType ?? null);
  const sortValue = (term: CampaignSearchTerm, key: SortKey): string | number | null => {
    switch (key) {
      case "name":
        return term.text;
      case "matchedTarget":
        return term.matchedTarget;
      case "adGroup":
        return term.adGroup;
      case "aiSuggestion":
        return term.aiSuggestion;
      default:
        return groupMetric(asMetricSource(term), key);
    }
  };
  const filtered = [
    ...all.filter(
      (term) =>
        term.text.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()) &&
        (match === "all" || matchOf(term) === match) &&
        (suggestion === "all" || term.aiSuggestion === suggestion),
    ),
  ].sort((a, b) => {
    const x = sortValue(a, sort.key);
    const y = sortValue(b, sort.key);
    if (x === null) return y === null ? 0 : 1;
    if (y === null) return -1;
    return (
      (typeof x === "string" && typeof y === "string"
        ? x.localeCompare(y)
        : Number(x) - Number(y)) * (sort.direction === "asc" ? 1 : -1)
    );
  });
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * size, currentPage * size);
  const total = aggregateGroups(filtered.map(asMetricSource));
  const currency = props.data?.currency ?? null;
  const previous = new Map(props.previousData?.searchTerms.map((term) => [term.id, term]));
  const previousTotal = aggregateGroups(
    filtered.flatMap((term) => {
      const old = previous.get(term.id);
      return old ? [asMetricSource(old)] : [];
    }),
  );
  const reset = () => setPage(1);
  const label = (key: SortKey | (typeof FIXED_COLUMNS)[number]): string =>
    key === "name"
      ? text("columns.name")
      : isMetric(key as Column)
        ? shared(`columns.${key}`)
        : text(`columns.${key}`);
  const matchLabel = (term: CampaignSearchTerm): string =>
    term.expressionType
      ? (expressionTypeLabel(term.expressionType) ?? term.expressionType)
      : term.keywordMatchType
        ? text(`matchSources.${term.keywordMatchType}`)
        : "—";
  const sortBy = (key: SortKey) => {
    setSort({ key, direction: sort.key === key && sort.direction === "asc" ? "desc" : "asc" });
    setPage(1);
  };

  const metric = (row: MetricSource, key: MetricColumn, old?: MetricSource) => {
    const value = groupMetric(row, key);
    const currencyMatches = props.previousData?.currency === currency && currency !== null;
    const usableOld = old && (!MONETARY_COLUMNS.includes(key) || currencyMatches) ? old : undefined;
    const before = usableOld ? groupMetric(usableOld, key) : null;
    const delta =
      value !== null && before !== null && before !== 0
        ? (value - before) / Math.abs(before)
        : null;
    return (
      <>
        <span className="font-mono tabular-nums text-ink">
          {formatGroupMetric(value, key, currency, i18n.language)}
        </span>
        {props.comparison && (
          <div
            className="font-mono text-[10px] leading-3.5 tabular-nums text-muted-foreground"
            title={shared("previous")}
          >
            {props.comparisonLoading ? (
              shared("loading")
            ) : props.comparisonError ? (
              "—"
            ) : (
              <>
                {usableOld ? formatGroupMetric(before, key, currency, i18n.language) : "—"} ·{" "}
                {delta === null
                  ? "—"
                  : new Intl.NumberFormat(i18n.language, {
                      style: "percent",
                      maximumFractionDigits: 2,
                      signDisplay: "exceptZero",
                    }).format(delta)}
              </>
            )}
          </div>
        )}
      </>
    );
  };
  const cellText = (term: CampaignSearchTerm, key: Column): string => {
    if (key === "matchedTarget") return term.matchedTarget ?? text("unmatched");
    if (key === "adGroup") return term.adGroup ?? "—";
    if (key === "aiSuggestion") return t(`searchTerms.suggestion.${term.aiSuggestion}`);
    return formatGroupMetric(groupMetric(asMetricSource(term), key), key, currency, i18n.language);
  };
  const cell = (term: CampaignSearchTerm, key: Column) => {
    if (isMetric(key)) {
      const old = previous.get(term.id);
      return metric(asMetricSource(term), key, old ? asMetricSource(old) : undefined);
    }
    if (key === "matchedTarget")
      return (
        <>
          <span className="block truncate" title={term.matchedTarget ?? undefined}>
            {term.matchedTarget ?? (
              <span className="text-muted-foreground">{text("unmatched")}</span>
            )}
          </span>
          <span className="block text-[10px] leading-3.5 text-muted-foreground">
            {matchLabel(term)}
          </span>
        </>
      );
    if (key === "adGroup")
      return term.adGroup ? (
        props.renderAdGroupLink ? (
          props.renderAdGroupLink(term.adGroupId, term.adGroup)
        ) : (
          <Link
            to="/campaigns/$id/ad-groups/$adGroupId"
            params={{ id: props.campaignId, adGroupId: term.adGroupId }}
            className="text-ink"
          >
            {term.adGroup}
          </Link>
        )
      ) : (
        "—"
      );
    return (
      <Badge variant={SUGGESTION_VARIANT[term.aiSuggestion]} size="compact" className="font-normal">
        {cellText(term, key)}
      </Badge>
    );
  };
  const exportCsv = () => {
    downloadCsv(
      `campaign-${props.campaignId}-search-terms.csv`,
      csvDocument([
        (["name", ...columns] as SortKey[]).map(label),
        ...filtered.map((term) => [term.text, ...columns.map((key) => cellText(term, key))]),
      ]),
    );
  };

  const sortHeader = (key: SortKey, className?: string) => {
    const active = sort.key === key;
    return (
      <TableHead
        key={key}
        className={className}
        aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
      >
        <button
          type="button"
          aria-label={label(key)}
          className="flex w-full min-w-0 items-center gap-1 truncate text-left uppercase outline-none focus-visible:ring-2 focus-visible:ring-ring"
          onClick={() => sortBy(key)}
        >
          <span className="truncate">{label(key)}</span>
          {active && sort.direction === "asc" ? (
            <IconArrowUp aria-hidden="true" className="size-3 shrink-0" />
          ) : active ? (
            <IconArrowDown aria-hidden="true" className="size-3 shrink-0" />
          ) : (
            <IconArrowsUpDown aria-hidden="true" className="size-3 shrink-0 opacity-45" />
          )}
        </button>
      </TableHead>
    );
  };

  const table = (
    <DataTable density="compact" stickyHeader aria-label={text("title")}>
      <TableHeader>
        <TableRow>
          {sortHeader("name", cn(NAME_STICKY, "z-30"))}
          {columns.map((key) =>
            sortHeader(key, isMetric(key) ? "text-right [&>button]:justify-end" : undefined),
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {visible.map((term) => (
          <TableRow key={term.id} className="hover:bg-table-row-hover">
            <TableCell className={cn(NAME_STICKY, "bg-surface")}>
              <span
                className="block max-w-80 truncate text-[11px] font-semibold leading-4 text-ink"
                title={term.text}
              >
                {term.text}
              </span>
            </TableCell>
            {columns.map((key) => (
              <TableCell key={key} className={cn(isMetric(key) && "text-right")}>
                {cell(term, key)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
      {filtered.length > 1 && (
        <TableFooter className="bg-subtle text-[11px] font-medium">
          <TableRow className="hover:bg-subtle">
            <TableCell className="sticky left-0 z-20 border-r border-line bg-subtle">
              {shared("total")}
            </TableCell>
            {columns.map((key) => (
              <TableCell key={key} className={cn(isMetric(key) && "text-right")}>
                {isMetric(key) ? metric(total, key, previousTotal) : ""}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      )}
    </DataTable>
  );

  const disabledControl = (labelText: string, reason: string) => (
    <UnavailableControl variant="outline" reason={reason}>
      {labelText}
    </UnavailableControl>
  );

  const toolbar = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {disabledControl(text("bulk"), text("writeUnavailable"))}
        {disabledControl("ASIN", shared("productUnavailable"))}
        <FilterSelect
          ariaLabel={text("filters.match")}
          items={MATCH_FILTERS.map((value) => ({
            value,
            label: value === "all" ? text("filters.match") : text(`matchSources.${value}`),
          }))}
          value={match}
          onValueChange={(value) => {
            setMatch(value as (typeof MATCH_FILTERS)[number]);
            reset();
          }}
        />
        <FilterSelect
          ariaLabel={t("searchTerms.filterOutcomeAria")}
          items={SUGGESTION_FILTERS.map((value) => ({
            value,
            label: t(`searchTerms.outcomeFilter.${value}`),
          }))}
          value={suggestion}
          onValueChange={(value) => {
            setSuggestion(value as (typeof SUGGESTION_FILTERS)[number]);
            reset();
          }}
        />
        {disabledControl(text("filters.type"), text("filterUnavailable"))}
        {disabledControl(text("filters.addedTarget"), text("filterUnavailable"))}
        {disabledControl(text("filters.addedNegative"), text("filterUnavailable"))}
        {disabledControl(text("filters.keywordList"), text("filterUnavailable"))}
        {disabledControl(shared("conditions"), shared("conditionsUnavailable"))}
      </div>
      <div className="flex items-center gap-2">
        <SearchInput
          aria-label={shared("search")}
          placeholder={shared("search")}
          value={search}
          showIcon
          textSize="compact"
          className="w-[200px]"
          onChange={(event) => {
            setSearch(event.target.value);
            reset();
          }}
        />
        <label
          htmlFor={`search-terms-comparison-${props.campaignId}`}
          className="flex items-center gap-1.5 whitespace-nowrap text-[11px] text-muted-foreground"
        >
          {shared("comparison")}
          <Switch
            id={`search-terms-comparison-${props.campaignId}`}
            aria-label={shared("comparison")}
            checked={props.comparison}
            onCheckedChange={props.onComparisonChange}
          />
        </label>
        <ControlButton
          variant="outline"
          controlSize="icon-toolbar"
          aria-label={shared("export")}
          title={shared("export")}
          disabled={filtered.length === 0 || props.isLoading || props.isError}
          onClick={exportCsv}
        >
          <IconDownload />
        </ControlButton>
        <ControlButton
          variant="outline"
          controlSize="icon-toolbar"
          aria-label={shared("fullscreen")}
          title={shared("fullscreen")}
          onClick={() => setExpanded(true)}
        >
          <IconMaximize />
        </ControlButton>
        <ColumnCustomizerDialog
          allColumns={ALL_COLUMNS}
          fixedColumns={FIXED_COLUMNS}
          defaultColumns={COLUMNS}
          visibleColumns={columns}
          label={(column) => label(column)}
          description={text("customColumnsDescription")}
          onApply={(next) =>
            setColumns(next.filter((column): column is Column => column !== "name"))
          }
        />
      </div>
    </>
  );

  const body = props.isLoading ? (
    <div className="flex flex-col gap-2 p-3.5" role="status" aria-label={shared("loading")}>
      {[1, 2, 3, 4].map((n) => (
        <Skeleton key={n} className="h-8 w-full" />
      ))}
    </div>
  ) : props.isError ? (
    <div className="p-3.5">
      <ErrorState
        message={text("loadFailed")}
        onRetry={props.onRetry}
        isRetrying={props.isFetching}
      />
    </div>
  ) : filtered.length === 0 ? (
    <div className="p-3.5">
      <EmptyState
        title={text("empty")}
        description={text(all.length ? "noMatches" : "emptyDescription")}
      />
    </div>
  ) : (
    table
  );

  return (
    <section
      aria-label={text("title")}
      className="flex flex-col gap-[14px]"
      aria-busy={props.isFetching}
    >
      <DataTableFrame
        toolbar={toolbar}
        footer={
          <DataTablePagination
            page={currentPage}
            pageCount={pages}
            pageSize={size}
            pageSizeOptions={PAGE_SIZES}
            summary={shared("count", { count: filtered.length })}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setSize(next);
              reset();
            }}
          />
        }
      >
        {props.comparison && props.comparisonError && (
          <div className="border-b border-line p-3.5">
            <ErrorState
              variant="inline"
              message={shared("comparisonFailed")}
              onRetry={props.onComparisonRetry}
              isRetrying={props.comparisonLoading}
            />
          </div>
        )}
        {body}
      </DataTableFrame>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogPopup className="w-[95vw] max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>{text("title")}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[80vh] overflow-auto px-6 pb-6">{table}</div>
        </DialogPopup>
      </Dialog>
    </section>
  );
}
